import cx from 'classnames'
import type React from 'react'
import { useController, useFormContext } from 'react-hook-form'

import { isFieldHighlighted } from '@helpers'

import type { TextareaFieldProps } from '../InputElements'
import { TextareaField } from '../InputElements'
import type { ReactHookFormFieldCommonProps } from './types'

type ReactHookFormTextareaFieldProps = Pick<
  TextareaFieldProps,
  | 'className'
  | 'disabled'
  | 'placeholder'
  | 'required'
  | 'value'
  | 'minRows'
  | 'maxRows'
  | 'label'
  | 'id'
  | 'endAdornment'
  | 'startAdornment'
  | 'onBlur'
> & {
  type?: never
} & ReactHookFormFieldCommonProps

export function ReactHookFormTextareaField({
  className,
  disabled = false,
  highlighted = false,
  isLabelHighlighted = false,
  name,
  shouldUnregister = false,
  transform,
  ...rest
}: ReactHookFormTextareaFieldProps) {
  const { control } = useFormContext()
  const {
    field: { onBlur, ...field },
    fieldState: { error },
    formState: { isSubmitting },
  } = useController({ control, name, shouldUnregister })

  const hasError = !!error
  const isFieldDisabled = disabled || isSubmitting

  return (
    <TextareaField
      {...field}
      className={cx(className, { 'form-control-error': hasError })}
      error={hasError}
      helperText={error?.message ?? ''}
      highlighted={isFieldHighlighted(highlighted, field.value)}
      isLabelHighlighted={isLabelHighlighted}
      disabled={isFieldDisabled}
      id={name}
      {...(transform && {
        value: transform.input(field.value),
        onChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => {
          field.onChange(transform.output(event.target.value))
          transform.positioner?.(event, transform.output(event.target.value), transform.input(field.value))
        },
      })}
      {...rest}
    />
  )
}
